import {
  CARD_SIZES,
  CardSize,
  ChartId,
  ChartType,
  coerceCardSize,
  DimensionId
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterNode } from '@blisspointmedia/bpm-types/dist/querying';
import { equals } from 'ramda';
import {
  CardDimensions,
  CardPosition,
  CardType,
  ChartCardConfig,
  DashboardCard,
  DashboardConfig,
  LayoutChange,
  PendingFilterEvent,
  PendingFilterFlow,
  SentFilterSave
} from './dashboard-types';
import {
  DEFAULT_CARD_SIZE,
  GRID_COLUMNS,
  GRID_GAP,
  ROW_PITCH,
  ROW_UNIT,
  SIZE_DIMENSIONS
} from './dashboard-constants';

export { GRID_COLUMNS, GRID_GAP, ROW_UNIT };

// Set `defaultFilters` only through here: it drops the stored value the save would otherwise restore.
export const withDefaultFilters = (
  config: DashboardConfig,
  defaultFilters: FilterNode[] | undefined
): DashboardConfig => {
  const { savedDefaultFiltersRaw, ...rest } = config;
  return { ...rest, defaultFilters };
};

// Set a card's opt-outs only through here, for the same reason as withDefaultFilters.
export const withCardIgnoreDashboardFilters = (
  card: ChartCardConfig,
  ignoreDashboardFilters: DimensionId[] | undefined
): ChartCardConfig => {
  const { savedIgnoreDashboardFiltersRaw, ...rest } = card;
  return { ...rest, ignoreDashboardFilters };
};

// The save payload for a pending agent change to the dashboard filters. With none pending the
// config is returned as is, keeping a stored value that failed validation.
export const withPendingDefaultFilters = (
  config: DashboardConfig,
  pendingDefaultFilters: FilterNode[] | undefined
): DashboardConfig =>
  pendingDefaultFilters === undefined ? config : withDefaultFilters(config, pendingDefaultFilters);

// Whether the saved default already holds the pending filters, as after a successful save's echo.
// An absent default is no filters, but a stored value that failed validation still needs replacing.
export const isPendingFilterSaved = (
  {
    defaultFilters,
    savedDefaultFiltersRaw
  }: Pick<DashboardConfig, 'defaultFilters' | 'savedDefaultFiltersRaw'>,
  pendingDefaultFilters: FilterNode[] | undefined
): boolean =>
  pendingDefaultFilters !== undefined &&
  savedDefaultFiltersRaw === undefined &&
  equals(defaultFilters ?? [], pendingDefaultFilters);

// Pending filters that would change neither the saved default nor the view (the view's own filters,
// not a preview) need no Save. Otherwise they stay pending, e.g. to replace a link's filters.
export const pendingFilterChangesNothing = (
  config: DashboardConfig,
  pendingDefaultFilters: FilterNode[],
  viewFilters: FilterNode[]
): boolean =>
  isPendingFilterSaved(config, pendingDefaultFilters) && equals(viewFilters, pendingDefaultFilters);

// A Save's echo, once a later saved config holds the filters it sent: the view takes them, and the
// pending filters clear only if no newer change (or Discard) replaced them. Undefined until then.
export const filterSaveEcho = (
  config: DashboardConfig,
  sent: SentFilterSave | undefined,
  pendingDefaultFilters: FilterNode[] | undefined
): { viewFilters: FilterNode[]; clearPending: boolean } | undefined => {
  if (!sent || config === sent.from || !isPendingFilterSaved(config, sent.filters))
    return undefined;
  return { viewFilters: sent.filters, clearPending: equals(pendingDefaultFilters, sent.filters) };
};

export const INITIAL_PENDING_FILTER_FLOW: PendingFilterFlow = {
  pending: undefined,
  sent: undefined,
  viewUpdate: undefined
};

// The agent's pending dashboard filters through Save, its echo or failure, and Discard. A Save the
// saved default already holds settles at once; any other waits for an echo holding what it sent.
export const pendingFilterFlow = (
  state: PendingFilterFlow,
  event: PendingFilterEvent
): PendingFilterFlow => {
  switch (event.kind) {
    case 'agentSetFilters':
      return pendingFilterChangesNothing(event.config, event.filters, event.viewFilters)
        ? { ...state, pending: undefined }
        : { ...state, pending: event.filters };
    case 'save': {
      const { pending } = state;
      if (!pending) return state;
      if (isPendingFilterSaved(event.config, pending)) {
        return { ...state, pending: undefined, viewUpdate: { filters: pending } };
      }
      return { ...state, sent: { from: event.config, filters: pending } };
    }
    case 'saveFailed':
      return state.sent ? { ...state, sent: undefined } : state;
    case 'echo': {
      const echo = filterSaveEcho(event.config, state.sent, state.pending);
      if (!echo) return state;
      return {
        ...state,
        sent: undefined,
        pending: echo.clearPending ? undefined : state.pending,
        viewUpdate: { filters: echo.viewFilters }
      };
    }
    case 'discard':
      return state.pending ? { ...state, pending: undefined } : state;
    default:
      return state;
  }
};

// The dashboard filters the agent's changes leave pending: the last one asked for, if any.
export const pendingDashboardFilters = (changes: LayoutChange[]): FilterNode[] | undefined =>
  changes.reduce<FilterNode[] | undefined>(
    (last, change) => (change.kind === 'setDashboardFilters' ? change.filters : last),
    undefined
  );

// A card's opt-out list, order-independent; absent and empty are the same.
const ignoreKey = (ignore: DimensionId[] | undefined): DimensionId[] =>
  [...(ignore ?? [])].sort((a, b) => a.localeCompare(b));

// A preset's footprint in grid cells: its column width, and its pixel height converted to whole
// rows. A heightPx that isn't a whole number of rows would round and leave the card permanently a
// few pixels out of line with its neighbours, so it throws rather than silently misaligning.
export function sizeDimensions(size: CardSize): CardDimensions {
  const { width, heightPx } = SIZE_DIMENSIONS[size];
  const height = (heightPx + GRID_GAP) / ROW_PITCH;
  if (!Number.isInteger(height)) {
    throw new Error(
      `SIZE_DIMENSIONS.${size}: ${heightPx}px is not a whole number of ${ROW_PITCH}px rows`
    );
  }
  return { width, height };
}

// The tallest footprint any preset uses. A card shorter than this leaves room below itself within
// the row's own height, which is exactly the space the slot-stacking rule in `autoCompact` offers to
// the next card -- derived from `SIZE_DIMENSIONS` (via `CARD_SIZES`, bpm-types' list of preset names)
// rather than hardcoded so a future preset (or a retuned height) changes who stacks without anyone
// touching the packer.
const TALLEST_PRESET_HEIGHT = Math.max(...CARD_SIZES.map(size => sizeDimensions(size).height));

// How deep the packing loops search, in ROWS. Expressed as a card count because the row unit is no
// longer a card: a bare `y < 100` meant ~100 cards when a card was one row, but only 20 standard or
// 6 wide ones at 10 and 15 rows each — past which adding a card silently fails.
// Evaluated at module load, so a SIZE_DIMENSIONS entry that isn't a whole number of rows throws on
// import rather than mid-render.
const MAX_SCAN_ROWS = 100 * sizeDimensions(DEFAULT_CARD_SIZE).height;

// Reading order: top-to-bottom, then left-to-right. Card positions are absolute, so this changes
// nothing on the normal grid — it matters at narrow widths, where cards auto-flow and DOM order
// becomes the order they appear in.
export function byGridOrder(a: DashboardCard, b: DashboardCard): number {
  return a.position.y !== b.position.y ? a.position.y - b.position.y : a.position.x - b.position.x;
}

export function getCardDimensions(card: DashboardCard): CardDimensions {
  return sizeDimensions(card.size);
}

export function hasCollision(card: DashboardCard, others: DashboardCard[]): boolean {
  const d = getCardDimensions(card);
  return others.some(other => {
    const od = getCardDimensions(other);
    return !(
      card.position.x + d.width <= other.position.x ||
      card.position.x >= other.position.x + od.width ||
      card.position.y + d.height <= other.position.y ||
      card.position.y >= other.position.y + od.height
    );
  });
}

export function isWithinBounds(card: DashboardCard): boolean {
  const dims = getCardDimensions(card);
  return (
    card.position.x >= 0 && card.position.x + dims.width <= GRID_COLUMNS && card.position.y >= 0
  );
}

export function isPositionAvailable(
  size: CardSize,
  position: CardPosition,
  existing: DashboardCard[]
): boolean {
  const test: DashboardCard = { id: 'test', type: CardType.CHART, size, position };
  return isWithinBounds(test) && !hasCollision(test, existing);
}

/**
 * Repack cards into rows, left to right in the ARRAY ORDER GIVEN, without ever pulling a later card
 * backwards into a hole an earlier one left open.
 *
 * Array order is the authoritative order for a dashboard's charts. It is the one durable channel
 * that survives a save/load round trip: `configToLayout` writes `config.cards` into
 * `layout.placements` in array order, that is what gets persisted as jsonb, and `dashboardToConfig`
 * reads it back via `migratedPlacements(d).map(placementToCard)` in the same order. There is
 * deliberately no separate "order" field — the array position IS the order.
 *
 * This function therefore must NEVER sort its input by position before packing. It USED TO: `const
 * sorted = [...cards].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)`.
 * That sort is exactly what let a RESIZE silently reorder the dashboard: widening a card to `wide`
 * forces its `x` back to 0 (a full-width card cannot start anywhere else), which changed its
 * `(y, x)` rank, and this function then faithfully packed the cards in that new, wrong rank —
 * turning "A, B, C" into "B, A, C" the moment B was widened and narrowed back. A drag is the ONE
 * case where the user deliberately reorders, and even that no longer goes through a position: the
 * drop resolves straight to an array index (see `resolveInsertion` and `insertAtIndex`, used by
 * `handleMoveCard`). `dashboardToConfig` USED TO also derive order from position on every
 * read, via a `.sort(byGridOrder)` before compaction, on the theory that a stored layout's position
 * always agreed with its reading order. Vertical stacking broke that theory — see the comment on
 * `dashboardToConfig` for why — so that sort is gone: array order is trusted as read off
 * `layout.placements`, never re-derived. Every write path, including this packer, must take array
 * order as given.
 *
 * The row packer advances strictly forward through the given order: it fills the current row left
 * to right until the next card would overflow `GRID_COLUMNS`, then opens a new row below the CURRENT
 * row's tallest card (not below every card seen so far, and not below just the last one). A row's
 * height is its tallest card, and the space below every shorter card in that row is deliberately left
 * empty rather than backfilled by anything that comes later -- that empty space is the whole point:
 * it is what lets a `one-third` sit beside a `standard`/`wide` without being stretched or having a
 * stranger card wedged underneath it. Trailing columns in an under-full row (e.g. a `one-third` +
 * `standard` using 10 of 12) are left empty for the same reason.
 *
 * This is the invariant every write path in DashboardBuilder and `compactBySection` relies on to
 * keep a saved layout matching the order the user built it in. Do not reintroduce a positional sort
 * here — that is precisely the bug described above, and it will resurface the instant any edit
 * clamps a position (a resize, a bounds fix) in a way that changes rank.
 *
 * VERTICAL STACKING. A card placed in a row's top tier opens a "slot" directly beneath itself --
 * same `x`, same width, running from its own bottom edge down to the row's tallest possible extent
 * (`TALLEST_PRESET_HEIGHT`) -- but only when the card is shorter than that tallest preset. With
 * today's presets that means only a `one-third` opens a slot (a `standard`/`wide` already IS the
 * tallest preset, so there is nothing left below it to offer).
 *
 * That slot is offered to the card that comes immediately next in ARRAY ORDER, and to no one else,
 * for exactly one turn. If that next card fits inside it, it is placed there, stacked, and it does
 * NOT open a slot of its own -- this produces pairs, never towers. If it does not fit, the slot
 * closes FOR GOOD, even though the space stays visibly empty. A later card must never be placed
 * there: reaching for it would mean pulling a card from further down the dashboard backwards, past
 * whatever card the user put in between, to fill a hole -- silently reordering the dashboard, which
 * is the exact bug `autoCompact` was rewritten to stop doing (see above). "The slot looks free" is
 * not a reason to fill it; only the one card that was actually next gets the offer.
 *
 * UNIFORM-ROW FLATTENING. When a row closes (a card no longer fits its remaining columns, or the
 * input runs out), and every card placed in that row -- tops and any stacked bottoms alike -- shares
 * the same height AND all of their widths sum to `GRID_COLUMNS` or less, the row is laid out side by
 * side instead of however the stacking left it. This is what keeps a row of three `one-third`s a clean
 * 3-across strip 5 rows tall, exactly as it renders today, instead of a lopsided stacked-pair-plus-
 * single-card block. It is deliberately narrow: it only ever re-lays a row that is ALREADY fully
 * accounted for by the cards placed in it (same height, fits across) -- it does not search for or
 * relocate any other card, so it can never become the backwards-reaching hole-filler the paragraph
 * above rules out. A row that doesn't meet both conditions (e.g. four `one-third`s: 16 columns don't fit
 * in 12) is left exactly as stacked.
 */
export function autoCompact(cards: DashboardCard[]): DashboardCard[] {
  if (cards.length === 0) return cards;

  const positions = new Map<string, CardPosition>();
  let rowTop = 0;
  packRows(cards).forEach(entries => {
    rowTop += closeRow(entries, rowTop, positions);
  });

  return cards.map(card => ({ ...card, position: requirePosition(positions, card.id) }));
}

// Anything the row packer can place. It needs nothing but the preset: a dashboard card on the way
// to being positioned, or an agent-emitted chart on the way to being sized.
interface Sized {
  size: CardSize;
}

// One top-tier placement in a packed row, and the item (if any) stacked in the slot it opened
// beneath itself. `width`/`topHeight` describe the top item; `bottom`'s own width and height are
// tracked separately since rule 2 only requires it to fit WITHIN the slot, not match it exactly.
interface RowEntry<T extends Sized> {
  x: number;
  width: number;
  top: T;
  topHeight: number;
  bottom?: { item: T; height: number; width: number };
}

// The slot most recently opened by a top-tier placement, still waiting to be offered to the next
// item. There is ever only one of these live at a time -- see the doc comment on `autoCompact`.
interface PendingSlot<T extends Sized> {
  entry: RowEntry<T>;
  width: number;
  height: number;
}

/**
 * Walk items in ARRAY ORDER and group them into the rows the packer forms, applying the
 * slot-stacking rule described on `autoCompact`.
 *
 * This is the SINGLE definition of where a row breaks. `autoCompact` turns these rows into
 * positions; `fillRowGaps` (useDashboard) reads them to tell a full row from one with an interior
 * hole. A second, flat left-to-right column count would disagree the moment two cards stack: it
 * reads `[one-third, one-third, standard]` as overflowing at 14 columns when the packer in fact fits all
 * three in one row, stacking the pair into a single 4-column slot -- and would then "fix" a hole
 * that was never there by widening both one-thirds.
 */
function packRows<T extends Sized>(items: T[]): RowEntry<T>[][] {
  const rows: RowEntry<T>[][] = [];
  let entries: RowEntry<T>[] = [];
  let colsUsed = 0;
  let pendingSlot: PendingSlot<T> | null = null;

  items.forEach(item => {
    const { width, height } = sizeDimensions(item.size);

    // Rule 2/3: the slot opened by the previous top-tier item is offered ONLY to this item, and
    // only once -- whether it fits or not, the offer is spent the instant we look at it.
    if (pendingSlot) {
      const slot = pendingSlot;
      pendingSlot = null;
      if (width <= slot.width && height <= slot.height) {
        slot.entry.bottom = { item, height, width };
        return; // Placed in the slot -- does not open a slot of its own (rule 3: pairs, not towers).
      }
      // Declined: the slot is gone for good (see the doc comment above). Fall through and place
      // this item through the normal top-tier path below, same as if no slot had ever opened.
    }

    if (colsUsed + width > GRID_COLUMNS) {
      rows.push(entries);
      entries = [];
      colsUsed = 0;
    }

    const entry: RowEntry<T> = { x: colsUsed, width, top: item, topHeight: height };
    entries.push(entry);
    colsUsed += width;

    // Rule 1: only an item shorter than the tallest preset leaves room below itself to offer.
    if (height < TALLEST_PRESET_HEIGHT) {
      pendingSlot = { entry, width, height: TALLEST_PRESET_HEIGHT - height };
    }
  });
  if (entries.length > 0) {
    rows.push(entries);
  }

  return rows;
}

// Every item placed in a row, tops and stacked bottoms alike, in placement order.
function rowMembers<T extends Sized>(
  entries: RowEntry<T>[]
): { item: T; width: number; height: number }[] {
  return entries.flatMap(entry => {
    const top = { item: entry.top, height: entry.topHeight, width: entry.width };
    return entry.bottom ? [top, entry.bottom] : [top];
  });
}

// Whether a row lays out as a single side-by-side strip -- see UNIFORM-ROW FLATTENING on
// `autoCompact`.
function flattensToStrip(members: { width: number; height: number }[]): boolean {
  return (
    members.every(member => member.height === members[0].height) &&
    members.reduce((sum, member) => sum + member.width, 0) <= GRID_COLUMNS
  );
}

// The columns a packed row actually occupies. Stacked items share their top's columns, so they cost
// nothing extra; a flattened row instead lays every member out side by side. `fillRowGaps` uses this
// to tell a full row from one with an interior hole.
function rowColumnsUsed<T extends Sized>(entries: RowEntry<T>[]): number {
  const members = rowMembers(entries);
  if (flattensToStrip(members)) {
    return members.reduce((sum, member) => sum + member.width, 0);
  }
  return entries.reduce((sum, entry) => sum + entry.width, 0);
}

// The rows `packRows` forms, as plain item lists -- what a caller that cares about row MEMBERSHIP
// rather than geometry wants. Paired with `rowColumnsUsed` so the two always describe the same row.
export function packedRows<T extends Sized>(items: T[]): { items: T[]; columnsUsed: number }[] {
  return packRows(items).map(entries => ({
    items: rowMembers(entries).map(member => member.item),
    columnsUsed: rowColumnsUsed(entries)
  }));
}

function requirePosition(positions: Map<string, CardPosition>, id: string): CardPosition {
  const position = positions.get(id);
  if (!position) {
    throw new Error(`autoCompact: no position was computed for card ${id}`);
  }
  return position;
}

// Position a packed row: lay it out as a single side-by-side strip when it flattens (see
// "UNIFORM-ROW FLATTENING" on `autoCompact`'s doc comment), else place each top at the column the
// packer gave it with its stacked partner directly beneath. Reports how far below `rowTop` the next
// row should start.
function closeRow(
  entries: RowEntry<DashboardCard>[],
  rowTop: number,
  positions: Map<string, CardPosition>
): number {
  if (entries.length === 0) return 0;

  const members = rowMembers(entries);

  if (flattensToStrip(members)) {
    let x = 0;
    members.forEach(member => {
      positions.set(member.item.id, { x, y: rowTop });
      x += member.width;
    });
    return members[0].height;
  }

  entries.forEach(entry => {
    positions.set(entry.top.id, { x: entry.x, y: rowTop });
    if (entry.bottom) {
      positions.set(entry.bottom.item.id, { x: entry.x, y: rowTop + entry.topHeight });
    }
  });

  return Math.max(...entries.map(entry => entry.topHeight + (entry.bottom?.height ?? 0)));
}

/**
 * Insert a dragged card among its peers at `index` — the drop's position in ARRAY order, as
 * `resolveInsertion` reads it off the pointer. A drag is the ONE operation where the user
 * deliberately means to reorder the dashboard; everywhere else (a resize, a remove), order is left
 * exactly as it was. Out-of-range indexes clamp to the ends. The caller must still run the result
 * through `autoCompact` — `moved`'s own position is stale until then.
 */
export function insertAtIndex(
  moved: DashboardCard,
  peers: DashboardCard[],
  index: number
): DashboardCard[] {
  const at = Math.max(0, Math.min(index, peers.length));
  return [...peers.slice(0, at), moved, ...peers.slice(at)];
}

export function findBestPosition(size: CardSize, existing: DashboardCard[]): CardPosition | null {
  const dims = sizeDimensions(size);
  for (let y = 0; y < MAX_SCAN_ROWS; y++) {
    for (let x = 0; x <= GRID_COLUMNS - dims.width; x++) {
      if (isPositionAvailable(size, { x, y }, existing)) return { x, y };
    }
  }
  return null;
}

/**
 * Close every gap and overlap in a layout, one grid at a time, without changing the order of the
 * charts.
 *
 * Built on `autoCompact`, which packs in ARRAY ORDER (see its doc comment) and so preserves reading
 * order by construction -- it never sorts by position. The deleted `tidyLayout` sorted by descending
 * area instead: with all presets sharing one height that is "widest first", which hoisted full-width
 * charts to the top of the dashboard. A dashboard is a narrative and its order belongs to whoever
 * built it, so repair must not rearrange it.
 *
 * Grouped by section because each `CardSection` renders its OWN `DashboardGrid` -- positions are
 * scoped per grid, so two cards in different sections may both legitimately sit at (0, 0), and
 * compacting them in one pass would treat that as a collision and push one of them down.
 *
 * The grouping is an implementation detail of the PACKING and must not leak into the returned array
 * order. This used to `flatMap` the groups, which returned all the loose cards first and each
 * section's cards hoisted into a contiguous block behind them -- so merely resizing or moving one
 * card silently re-ranked every sectioned card in the dashboard. Array order is the persisted record
 * of what the user built (see `autoCompact`'s doc comment), so every card is written back at the
 * index it came in at; only its position changes.
 */
export function compactBySection(cards: DashboardCard[]): DashboardCard[] {
  if (cards.length === 0) return cards;
  const bySection = new Map<string | null, DashboardCard[]>();
  cards.forEach(card => {
    const key = card.sectionId ?? null;
    const group = bySection.get(key);
    if (group) {
      group.push(card);
    } else {
      bySection.set(key, [card]);
    }
  });
  const packed = new Map<string, DashboardCard>();
  bySection.forEach(group => autoCompact(group).forEach(card => packed.set(card.id, card)));
  return cards.map(card => packed.get(card.id) ?? card);
}

// Order-independent signature of chart placements, opt-outs included. Ignores card ids, which a
// save's echo regenerates, so the echo isn't mistaken for an external change that resets undo.
export function placementSignature(cards: DashboardCard[]): string {
  return JSON.stringify(
    cards
      .filter(c => c.type === CardType.CHART && c.config)
      .map(c => {
        const { chartId, ignoreDashboardFilters, savedIgnoreDashboardFiltersRaw } =
          c.config as ChartCardConfig;
        const ignore = ignoreKey(ignoreDashboardFilters);
        const raw = savedIgnoreDashboardFiltersRaw !== undefined;
        return { chartId, x: c.position.x, y: c.position.y, size: c.size, ignore, raw };
      })
      .sort((a, b) => a.chartId.localeCompare(b.chartId))
  );
}

// Take a card off the layout and compact the survivors, so no hole is left behind. Compaction goes
// through `autoCompact`, which packs in ARRAY ORDER (see its doc comment) -- the survivors keep
// their relative order and are simply repacked into the gap the removed card left, never re-derived
// from a position. Same reference back when the card isn't there.
export function removeCard(cards: DashboardCard[], id: string): DashboardCard[] {
  if (!cards.some(c => c.id === id)) return cards;
  return autoCompact(cards.filter(c => c.id !== id));
}

const chartOf = (c: DashboardCard) => (c.type === CardType.CHART ? c.config?.chartId : undefined);

// The saved card a card on screen stands for: the k-th placement of its chart on screen is the k-th
// saved one, if saved. Ids are placement-derived and drift once a removal shifts the saved indexes.
export const savedCardFor = (
  saved: DashboardCard[],
  shown: DashboardCard[],
  id: string
): DashboardCard | undefined => {
  const card = shown.find(c => c.id === id);
  if (!card) return undefined;
  const chartId = chartOf(card);
  if (!chartId) return saved.find(c => c.id === id);
  const ordinal = shown.filter(c => chartOf(c) === chartId).indexOf(card);
  const savedOfChart = saved.filter(c => chartOf(c) === chartId);
  return savedOfChart[ordinal];
};

// The saved layout without a card picked on screen, leaving out every other change the screen holds.
export const removeShownCardFromSaved = (
  saved: DashboardCard[],
  shown: DashboardCard[],
  id: string
): DashboardCard[] => {
  const match = savedCardFor(saved, shown, id);
  return match ? removeCard(saved, match.id) : saved;
};

/**
 * Move a card to a 1-based SLOT, counting through the ARRAY ORDER of loose cards (not their grid
 * position), then repack with `compactBySection`. A slot past the end means last. Only loose cards
 * reorder: a sectioned card is left alone, since sections render their own grid and are never
 * reordered from here. Same reference back when nothing moves.
 *
 * A `move` is one of only two operations allowed to derive a NEW array order from something other
 * than the array itself (the other is a drag, via `insertAtIndex`) -- because here the slot
 * number IS the user's (or agent's) deliberate reorder request. Once that new order is decided,
 * though, placement reverts to the ordinary rule: `compactBySection` packs strictly in that array
 * order (see `autoCompact`'s doc comment). This function must NOT go back to sorting loose cards by
 * `byGridOrder` before slicing, and must NOT hand placement to a first-fit scan like the deleted
 * `findBestPosition` walk this used to use -- either one re-derives order from grid position after
 * the fact, which is exactly the bug `autoCompact` was rewritten to stop doing, and it would let a
 * later resize or bounds-clamp silently reorder the dashboard again.
 *
 * A sectioned card keeps its own INDEX in the returned array, not just its own grid position --
 * `cards.map` below walks the original array once and only ever substitutes a LOOSE slot with the
 * next entry off `reordered`, leaving every sectioned card exactly where it already was. That
 * matters because array order is what gets persisted (`layout.placements`), and sections render
 * `[]` today (see `dashboardToConfig`) -- the moment that changes, hoisting sectioned cards to the
 * front of the array (as concatenating `cards.filter(c => c.sectionId)` ahead of the loose cards
 * would) silently churns the stored order on every `move`, which is exactly the kind of
 * nobody-asked-for-it reorder this whole module exists to prevent. `compactBySection` preserves
 * those indices too, for the same reason.
 *
 * Precisely: every sectioned card keeps its exact index, and the loose cards other than the moved
 * one keep their relative order. The loose cards between the moved card's old and new rank do shift
 * index by one -- that is what a reorder IS -- but no card is re-ranked relative to another except
 * the one the caller asked to move.
 */
export function moveCardToSlot(cards: DashboardCard[], id: string, slot: number): DashboardCard[] {
  const card = cards.find(c => c.id === id);
  if (!card || card.sectionId) return cards;
  const loose = cards.filter(c => !c.sectionId);
  const rest = loose.filter(c => c.id !== id);
  const at = Math.min(Math.max(Math.trunc(slot) - 1, 0), rest.length);
  if (loose.indexOf(card) === at) return cards;

  const reordered = [...rest.slice(0, at), card, ...rest.slice(at)];
  let next = 0;
  const merged = cards.map(c => (c.sectionId ? c : reordered[next++]));
  return compactBySection(merged);
}

// An empty list clears the opt-out. Same reference back when the card already has that opt-out.
function setCardFilterOptOut(
  cards: DashboardCard[],
  id: string,
  ignore: DimensionId[]
): DashboardCard[] {
  const config = cards.find(c => c.id === id)?.config;
  if (!config) return cards;
  const unchanged =
    config.savedIgnoreDashboardFiltersRaw === undefined &&
    JSON.stringify(ignoreKey(config.ignoreDashboardFilters)) === JSON.stringify(ignoreKey(ignore));
  if (unchanged) return cards;
  const next = withCardIgnoreDashboardFilters(config, ignore.length > 0 ? ignore : undefined);
  return cards.map(card => (card.id === id ? { ...card, config: next } : card));
}

// Apply the agent's layout changes in call order, each to the layout the previous one left, so
// "move A to 1, then B to 2" lands both. Each chart resolves to its first card; a chart that isn't
// on the layout is skipped rather than guessed at. `typeOf` is threaded into the resize case so an
// agent-driven resize is coerced through the same chart-type size matrix as a menu resize (see
// `resizeCardInList`'s doc comment) -- without it, the agent could set a size the matrix forbids for
// that chart type. Same reference back when nothing changed.
export function applyLayoutChanges(
  cards: DashboardCard[],
  changes: LayoutChange[],
  typeOf?: (chartId: ChartId) => ChartType | undefined
): DashboardCard[] {
  return changes.reduce((list, change) => {
    if (change.kind === 'setDashboardFilters') return list;
    const cardId = list.find(
      c => c.type === CardType.CHART && c.config?.chartId === change.chartId
    )?.id;
    if (!cardId) return list;
    switch (change.kind) {
      case 'setCardFilterOptOut':
        return setCardFilterOptOut(list, cardId, change.ignore);
      case 'resize':
        return resizeCardInList(list, cardId, change.size, typeOf);
      case 'remove':
        return removeCard(list, cardId);
      case 'move':
        return moveCardToSlot(list, cardId, change.slot);
      default:
        return list;
    }
  }, cards);
}

/**
 * Fold a layout change made OUTSIDE the builder (the agent appended or removed a chart, so the
 * dashboard refetched) into the layout on screen, which may hold unsaved changes. Three-way, against
 * the layout persisted BEFORE the refetch: charts the server added are placed into the buffer, charts
 * it dropped are removed from it, and everything else the buffer holds -- positions, sizes, its own
 * removals -- is kept. With nothing unsaved the buffer IS the old persisted layout, so the server's
 * is taken as-is. Keyed by chart: card ids are placement-derived and don't survive a refetch.
 *
 * A chart the server ADDED is appended at the END of the array and placed by `compactBySection`,
 * never by a first-fit scan (the deleted `findBestPosition` walk this used to use). `compactBySection`
 * packs strictly in ARRAY ORDER (see `autoCompact`'s doc comment), so every chart the buffer already
 * held keeps the same relative order it had -- first-fit would instead search for the first open
 * grid hole, which can seat the new chart ahead of cards the user or agent deliberately ordered
 * after it, silently reordering the dashboard.
 */
export function mergeExternalLayout(
  previous: DashboardCard[],
  persisted: DashboardCard[],
  shown: DashboardCard[]
): DashboardCard[] {
  if (placementSignature(shown) === placementSignature(previous)) return persisted;

  const before = new Set(previous.map(chartOf));
  const now = new Set(persisted.map(chartOf));

  const kept = shown.filter(c => {
    const chartId = chartOf(c);
    return !(chartId && before.has(chartId) && !now.has(chartId));
  });
  const merged = kept.length < shown.length ? autoCompact(kept) : [...kept];

  const added = persisted.filter(c => {
    const chartId = chartOf(c);
    return chartId && !before.has(chartId);
  });
  return added.length === 0 ? merged : compactBySection([...merged, ...added]);
}

/**
 * Resize a card to another preset, replacing it IN PLACE in the array and packing the result.
 *
 * Array order is authoritative (see `autoCompact`'s doc comment). A widening resize can force
 * `position.x` back to 0 — a `wide` card cannot start anywhere else — and that x-clamp is exactly
 * what used to reorder the whole dashboard: the old implementation rebuilt the array as
 * `[...others, ...peers]` (cards from other sections, then this card's resized section, packed),
 * which moves the resized card's whole group to the end of the array regardless of where it
 * started — a positional reorder wearing a different hat. `list.map` instead keeps every card,
 * including the resized one, at its original INDEX; resizing changes its size, never its rank.
 * `compactBySection` (not the bare `autoCompact`) is used to pack the result because the caller may
 * pass the whole dashboard's cards across multiple sections in one list, and positions are scoped
 * per section (see `compactBySection`'s own doc comment).
 *
 * Extracted here (rather than kept as a DashboardBuilder-local closure) both because it is pure —
 * needs nothing but its arguments — and so it has a seam for a direct unit test: exporting a
 * component's internal handler just to test it would leak an implementation detail across the
 * module boundary, where this belongs with the rest of the ordering logic it must stay consistent
 * with.
 *
 * This is the SINGLE funnel both the card's ⋮ menu (`handleChangeCardSize`) and the agent's
 * chat-driven resize (`applyLayoutChanges`, via `usePendingLayoutChanges`) pass through, which is
 * exactly why the size-matrix guard lives here rather than at either call site: the menu can only
 * offer legal sizes today, but the agent echoes whatever size it is told to set, with nothing yet
 * stopping it from setting a `table` to `one-third`. `typeOf` resolves the card's chart type so the
 * requested size can be run through
 * `coerceCardSize` before it is applied.
 *
 * DELIBERATE RULE: when the chart's type cannot be resolved — no `typeOf` was passed, the card has
 * no `chartId`, or `typeOf` itself returns `undefined` for that id — the requested size is applied
 * UNCHANGED, never defaulted. An unresolvable type means a 403-restricted or deleted chart; silently
 * substituting a default size would resize a card the current user cannot even see.
 */
export function resizeCardInList(
  list: DashboardCard[],
  id: string,
  size: CardSize,
  typeOf?: (chartId: ChartId) => ChartType | undefined
): DashboardCard[] {
  const card = list.find(c => c.id === id);
  if (!card) return list;
  const chartId = card.config?.chartId;
  const type = chartId ? typeOf?.(chartId) : undefined;
  const resolvedSize = type ? coerceCardSize(type, size) : size;
  if (card.size === resolvedSize) return list;
  const resized: DashboardCard = {
    ...card,
    size: resolvedSize,
    // A widening card at x=2 would otherwise run off the right edge, which isWithinBounds rejects.
    position: {
      ...card.position,
      x: Math.min(card.position.x, GRID_COLUMNS - sizeDimensions(resolvedSize).width)
    }
  };
  return compactBySection(list.map(c => (c.id === id ? resized : c)));
}

export function toGridPlacement(
  position: CardPosition,
  dims: CardDimensions
): { gridColumn: string; gridRow: string } {
  return {
    gridColumn: `${position.x + 1} / span ${dims.width}`,
    gridRow: `${position.y + 1} / span ${dims.height}`
  };
}

/**
 * The date link toggle's label, which has to describe three different states rather than two.
 *
 * A card can stop following the dashboard's date for two unrelated reasons: the viewer broke it
 * away with this toggle (`hasOwnRange`), or the chart itself declares no `dateRange` control and so
 * never participates (`followsPicker` false — see `applyDateRangeOverrides`). Both render the
 * unlinked icon, but only the first is something the toggle can undo: clearing an override returns
 * the card to whichever window it would otherwise show, while a chart that pins its own dates
 * cannot be made to follow at all.
 */
export function pinnedDateAriaLabel({
  hasOwnRange,
  followsPicker
}: {
  hasOwnRange: boolean;
  followsPicker: boolean;
}): string {
  if (hasOwnRange) {
    return followsPicker
      ? "Unlinked from the dashboard's date picker. Click to link."
      : "Unlinked from the dashboard's date picker. Click to return to this chart's own dates.";
  }
  return followsPicker
    ? "Linked to the dashboard's date picker. Click to unlink."
    : "Unlinked from the dashboard's date picker. This chart always uses its own dates.";
}

interface CardDatePickerWidths {
  // The title's full one-line width, info icon included.
  titleWidth?: number;
  // The width the title has to lay out in right now.
  titleSlotWidth?: number;
  // The date picker as currently drawn (collapsed or expanded).
  pickerWidth?: number;
  // The date picker as it WOULD be drawn expanded, range spelled out.
  expandedPickerWidth?: number;
}

/**
 * Whether a card's date picker should collapse to its icon: yes if expanding it would push the title
 * onto a second line.
 *
 * Asked counterfactually -- the title's room is corrected to what it would be with the picker
 * expanded -- rather than "is the title wrapping now". Collapsing hands the title the picker's width
 * back, so the direct question would answer itself differently on alternate renders and flip the
 * picker back and forth. Anything not yet measured keeps the full picker.
 */
export function shouldCollapseCardDatePicker({
  titleWidth,
  titleSlotWidth,
  pickerWidth,
  expandedPickerWidth
}: CardDatePickerWidths): boolean {
  if (
    titleWidth === undefined ||
    titleSlotWidth === undefined ||
    pickerWidth === undefined ||
    expandedPickerWidth === undefined
  ) {
    return false;
  }
  const slotIfExpanded = titleSlotWidth - (expandedPickerWidth - pickerWidth);
  return titleWidth > slotIfExpanded;
}
